import React from 'react';
import { Chart as ReactChartJSLineChart } from "react-chartjs-2";
import { Chart as ChartJS } from "chart.js";
import { Timeseries } from "types/analytics";
import { usePlatforms } from './Filters/hooks/usePlatforms';
import { ProgressSpinner } from "primereact/progressspinner";
import styled from "styled-components";
import moment from "moment/moment";
import { getRandomColor } from './utils';

interface LineChartProps {
  timeseries?: Timeseries[];
}

const verticalLinePlugin = {
  id: "verticalLine",
  afterDraw: (chart: any) => {
    const tooltip = chart.tooltip;
    if (!tooltip?.getActiveElements) return;
    const activeElements = tooltip.getActiveElements();
    if (!activeElements || activeElements.length === 0) return;

    const canvas = chart.canvas;
    const rect = canvas.getBoundingClientRect();

    // Координаты курсора относительно canvas
    const mouseY = chart._lastEvent?.y ?? null;
    if (mouseY == null) return;

    // Находим ближайшую точку по Y
    let closest = activeElements[0];
    let minDist = Infinity;
    activeElements.forEach((el: any) => {
      const y = el.element.y;
      const dist = Math.abs(mouseY - y);
      if (dist < minDist) {
        minDist = dist;
        closest = el;
      }
    });

    const x = closest.element.x;
    const topY = chart.scales.y?.top;
    const bottomY = chart.scales.y?.bottom;

    const ctx = chart.ctx;
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(x, topY);
    ctx.lineTo(x, bottomY);
    ctx.lineWidth = 1.5;
    ctx.strokeStyle = "#00000033";
    ctx.setLineDash([4, 4]);
    ctx.stroke();
    ctx.restore();
  },
};

ChartJS.register(verticalLinePlugin);

export const LineChart: React.FC<LineChartProps> = props => {
  const { timeseries } = props;
  const { platforms, fetching } = usePlatforms();
  const tooltipRef = React.useRef<HTMLDivElement | null>(null);
  const chartRef = React.useRef<any>(null);

  const platformsMap: Record<string, string> = React.useMemo(() =>
    platforms.reduce((result, platform) => {
      return { ...result, [platform.id]: platform.name };
    }, {}),
  [platforms]);

  const labels: any[] = React.useMemo(() => {
    const set = new Set();

    if (timeseries?.length === 0) {
      set.add(moment().format("YYYY-MM-DD"));
      set.add(moment().add('day').format("YYYY-MM-DD"));
    } else {
      timeseries?.forEach(timeserie => {
        set.add(timeserie.date);
      });
    }
    return Array.from(set.values());
  }, [timeseries]);

  const datasets = React.useMemo(() => {
    const data = timeseries?.reduce((state: Record<string, Record<string, string>>, item: Timeseries) => {
      return {
        ...state,
        [item.platform_id]: {
          ...state[item.platform_id],
          [item.date]: item.plays,
        }
      };
    }, {});

    const datasets = Object.entries(data ?? {}).map(([key, item]: any) => {
      const color = getRandomColor() || 'rgb(255, 99, 132)';
      return {
        type: 'line' as const,
        borderWidth: 2,
        borderColor: color,
        backgroundColor: color,
        fill: false,
        tension: 0.4,
        data: labels.map(label => item[label] || 0),
        name: platformsMap[key] || '-',
        getCurrentDateValue: (date: string) => {
          return item[date] || 0;
        }
      };
    });

    return datasets.length ? datasets : [{
      type: 'line' as const,
      fill: false,
      data: [],
    }];
  }, [labels, platformsMap]);

  React.useEffect(() => {
    const chart = chartRef.current?.chart || chartRef.current; // разная обёртка в зависимости от версии react-chartjs-2
    if (!chart) return;

    const canvas = chart.canvas;
    if (!canvas) return;

    const handleMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect?.left;
      const y = e.clientY - rect?.top;

      const elements = chart.getElementsAtEventForMode(
        e,
        "index",
        { intersect: false },
        false
      );

      if (!elements || elements.length === 0) {
        if (chart.tooltip && typeof chart.tooltip.setActiveElements === "function") {
          chart.tooltip.setActiveElements([], { x, y });
          chart.update("none");
        } else if (typeof chart.setActiveElements === "function") {
          chart.setActiveElements([]);
          chart.update("none");
        }
        return;
      }

      let closest = elements[0];
      let minDist = Infinity;
      elements.forEach((el: any) => {
        const elY = el.element?.y ?? el.y;
        const dist = Math.abs(elY - y);
        if (dist < minDist) {
          minDist = dist;
          closest = el;
        }
      });

      const active = [{ datasetIndex: closest.datasetIndex, index: closest.index }];

      if (chart.tooltip && typeof chart.tooltip.setActiveElements === "function") {
        chart.tooltip.setActiveElements(active, { x, y });
      } else if (typeof chart.setActiveElements === "function") {
        chart.setActiveElements(active);
      }

      chart.update("none");
    };

    canvas.addEventListener("mousemove", handleMove);
    return () => canvas.removeEventListener("mousemove", handleMove);
  }, [datasets, labels]);

  React.useEffect(() => {
    const chart = chartRef.current?.chart || chartRef.current;
    if (!chart) return;

    let rafId: number;

    const forceResize = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        chart.resize();
      });
    };

    window.addEventListener("resize", forceResize);
    forceResize();

    return () => {
      window.removeEventListener("resize", forceResize);
      cancelAnimationFrame(rafId);
    };
  }, []);

  if (fetching) {
    return (
      <Container>
        <ProgressSpinner />
      </Container>
    );
  }

  return (
    <Container>
      <ReactChartJSLineChart
        ref={chartRef}
        type="line"
        data={{ labels, datasets }}
        options={{
          maintainAspectRatio: false,
          interaction: {
            mode: "index" as const,
            intersect: false,
          },
          responsive: true,
          plugins: {
            tooltip: {
              enabled: false,
              // external: (context: any) => {
              //   const tooltipEl = tooltipRef.current;
              //   const tooltipModel = context.tooltip;
              //
              //   if (!tooltipEl) return;
              //
              //   if (tooltipModel.opacity === 0) {
              //     tooltipEl.style.opacity = "0";
              //     return;
              //   }
              //
              //   const date = tooltipModel.title?.[0] ?? "";
              //   let title = `<div class="title">${date}</div>`;
              //   let innerHtml = ``;
              //
              //   let point = null;
              //   if (tooltipModel.dataPoints?.length) {
              //     const mouseY = context.chart._lastEvent?.y ?? null;
              //     if (mouseY != null) {
              //       let minDist = Infinity;
              //       tooltipModel.dataPoints.forEach((p: any) => {
              //         const y = p.element.y;
              //         const dist = Math.abs(mouseY - y);
              //         if (dist < minDist) {
              //           minDist = dist;
              //           point = p;
              //         }
              //       });
              //     } else {
              //       point = tooltipModel.dataPoints[0];
              //     }
              //   }
              //   const activeElements = tooltipModel.getActiveElements();
              //
              //   const hoveredPointName = point ? point.dataset.name : null;
              //   datasets.forEach((item: any) => {
              //     innerHtml += `
              //       <div class="row" style="opacity: ${item.name === hoveredPointName ? 1 : 0.4}">
              //         <div class="info">
              //           <span class="dot" style="background: ${item.backgroundColor}"></span>
              //           <span class="label">${item.name}</span>
              //         </div>
              //         <span class="value" style="color: ${item.backgroundColor}">
              //           ${item.getCurrentDateValue(date)}
              //         </span>
              //       </div>`;
              //   });
              //
              //   tooltipEl.innerHTML = `
              //     <div>
              //       ${title}
              //       <div class="content">
              //         ${innerHtml}
              //       </div>
              //     </div>
              //   `;
              //
              //   const { offsetLeft: positionX, offsetTop: positionY } = context.chart.canvas;
              //   tooltipEl.style.opacity = "1";
              //   tooltipEl.style.left = positionX + tooltipModel.caretX + "px";
              //   tooltipEl.style.top = positionY + tooltipModel.caretY + "px";
              // },
            },
            legend: {
              display: false,
            },
          },
        }}
      />
      <Tooltip ref={tooltipRef} />
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  width: 100%;
  height: 280px;
  position: relative;

  min-width: 0;

  canvas {
    width: 100% !important;
    max-width: 100% !important;
  }
`;


const Tooltip = styled.div`
  width: 210px;
  display: flex;
  flex-direction: column;
  position: absolute;
  opacity: 0;
  pointer-events: none;
  transform: translate(10px, -100%);
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 0 10px 3px #BCBCBFAD;
  white-space: nowrap;

  .title {
    width: 100%;
    font-family: SF Pro Display;
    font-weight: 500;
    font-style: Medium;
    font-size: 13px;
    leading-trim: NONE;
    line-height: 18px;
    letter-spacing: 0%;
    vertical-align: middle;
    color: #00000080;
    padding: 8px 12px;
    border-bottom: 1px solid #00000033;
  }

  .content {
    padding: 8px 12px;
    display: flex;
    gap: 15px;
    flex-direction: column;
  }

  .row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 4px;
    margin: 0 0;
    opacity: 0.4;
  }

  .info {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 4px;
  }

  .dot {
    display: inline-block;
    width: 11px;
    height: 11px;
    border-radius: 50%;
  }

  .label {
    font-family: SF Pro Display;
    font-weight: 400;
    font-style: Regular;
    font-size: 13px;
    leading-trim: NONE;
    line-height: 15px;
    letter-spacing: 0%;
    vertical-align: middle;
    color: #000000;
  }

  .value {
    font-family: SF Pro Display;
    font-weight: 400;
    font-style: Regular;
    font-size: 13px;
    leading-trim: NONE;
    line-height: 15px;
    letter-spacing: 0%;
    vertical-align: middle;
  }
`;

const Title = styled.div``;
